import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import "./RubberSegment.css";

const EASE_OUT = [0.23, 1, 0.32, 1];
const SPRING_UI = { type: "spring", duration: 0.3, bounce: 0 };
const SPRING_MOMENTUM = { type: "spring", duration: 0.4, bounce: 0.2 };
const SPRING_RELAX = { type: "spring", duration: 0.16, bounce: 0 };
const MAX_VELOCITY = 2000;
const SIZES = {
    sm: { height: 28, font: 12, pad: 10, min: 36 },
    md: { height: 36, font: 13, pad: 14, min: 44 },
    lg: { height: 44, font: 14, pad: 18, min: 48 },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const rubber = (over, dim) => (over * dim * 0.55) / (dim + 0.55 * Math.abs(over));
const project = (velocity, glide) => {
    if (glide <= 0) return 0;
    const d = 1 - 0.1 * Math.pow(0.05, glide / 100);
    return ((velocity / 1000) * d) / (1 - d);
};
const velocityOf = (history, now) => {
    const recent = history.filter(([time]) => now - time <= 100);
    if (recent.length < 2) return 0;
    const [t0, x0] = recent[0];
    const [t1, x1] = recent[recent.length - 1];
    return t1 - t0 >= 8 ? ((x1 - x0) / (t1 - t0)) * 1000 : 0;
};
const nearestSlot = (slots, x) => {
    let best = 0;
    for (let i = 1; i < slots.length; i++) {
        if (Math.abs((slots[i].l + slots[i].r) / 2 - x) < Math.abs((slots[best].l + slots[best].r) / 2 - x)) best = i;
    }
    return best;
};

/**
 * Items are strings or { value, label, icon? } objects. Use value/onChange(value, index)
 * for controlled selection, or defaultValue for internal state.
 */
export default function RubberSegment({
    items,
    value,
    defaultValue,
    onChange,
    trackColor = "#27272a",
    thumbColor = "#fafafa",
    textColor = "#fafafa",
    activeTextColor = "#18181b",
    size = "md",
    radius = 10,
    inset = 3,
    equalSlots = true,
    stretch = 100,
    squash = 3,
    speed = 1,
    glide = 75,
    draggable = true,
    disabled = false,
    className = "",
    "aria-label": ariaLabel = "Segmented control",
}) {
    const list = items.map(item => typeof item === "string" ? { value: item, label: item } : item);
    const [inner, setInner] = useState(defaultValue ?? list[0]?.value);
    const current = value !== undefined ? value : inner;
    const index = Math.max(0, list.findIndex(item => item.value === current));
    const reduce = useReducedMotion();
    const trackRef = useRef(null);
    const itemRefs = useRef([]);
    const slots = useRef([]);
    const box = useRef(null);
    const committed = useRef(index);
    const handoff = useRef(0);
    const drag = useRef(null);
    const suppressClick = useRef(false);
    const gen = useRef(0);
    const edgeL = useMotionValue(0);
    const edgeR = useMotionValue(0);
    const innerW = useMotionValue(0);
    const thumbRadius = Math.max(0, radius - inset);
    const getClipPath = () => `inset(0 ${Math.max(0, innerW.get() - edgeR.get())}px 0 ${Math.max(0, edgeL.get())}px round ${thumbRadius}px)`;
    const clipPath = useTransform(getClipPath);
    const t = seconds => seconds / Math.max(0.01, speed);

    const jumpTo = i => {
        const slot = slots.current[i];
        if (!slot) return;
        clearTimeout(handoff.current);
        gen.current += 1;
        edgeL.jump(slot.l);
        edgeR.jump(slot.r);
        // Strict Mode can cancel the derived value's queued update during remount.
        clipPath.set(getClipPath());
    };

    const measure = () => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        box.current = rect;
        slots.current = list.map((_, i) => {
            const item = itemRefs.current[i];
            return { l: item.offsetLeft - inset, r: item.offsetLeft + item.offsetWidth - inset };
        });
        innerW.set(track.clientWidth - inset * 2);
        jumpTo(committed.current);
    };

    const listKey = JSON.stringify(list.map(item => item.value));
    useLayoutEffect(() => {
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(trackRef.current);
        let mounted = true;
        document.fonts?.ready.then(() => {
            if (mounted) measure();
        });
        return () => {
            mounted = false;
            observer.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listKey, size, inset, equalSlots]);

    useEffect(() => {
        if (!drag.current && committed.current !== index) {
            committed.current = index;
            jumpTo(index);
        }
    });

    useEffect(() => () => {
        clearTimeout(handoff.current);
        gen.current += 1;
        edgeL.stop();
        edgeR.stop();
    }, [edgeL, edgeR]);

    const commit = i => {
        committed.current = i;
        if (i === index) return;
        if (value === undefined) setInner(list[i].value);
        onChange?.(list[i].value, i);
    };

    const land = (to, velocity, flick, withSquash) => {
        const slot = slots.current[to];
        if (!slot) return;
        const generation = ++gen.current;
        const dir = Math.sign((slot.l + slot.r) / 2 - (edgeL.get() + edgeR.get()) / 2) || 1;
        const [lead, leadTo, trail, trailTo] = dir > 0
            ? [edgeR, slot.r, edgeL, slot.l] : [edgeL, slot.l, edgeR, slot.r];
        const velocityFor = mv => clamp(velocity === null ? mv.getVelocity() : velocity, -MAX_VELOCITY, MAX_VELOCITY);
        animate(lead, leadTo, {
            ...(flick ? SPRING_MOMENTUM : SPRING_UI),
            duration: t(flick ? 0.4 : 0.3),
            velocity: velocityFor(lead),
        });
        const trailVelocity = velocityFor(trail);
        if (!withSquash || squash <= 0) {
            animate(trail, trailTo, { ...SPRING_UI, duration: t(0.3), velocity: trailVelocity });
            return;
        }
        animate(trail, trailTo + dir * squash, { ...SPRING_UI, duration: t(0.3), velocity: trailVelocity }).then(() => {
            if (gen.current === generation) animate(trail, trailTo, { ...SPRING_RELAX, duration: t(0.16) });
        });
    };

    const travel = (from, to) => {
        const a = slots.current[from];
        const b = slots.current[to];
        if (!a || !b) return;
        clearTimeout(handoff.current);
        gen.current += 1;
        if (reduce) {
            jumpTo(to);
            return;
        }
        const u = stretch / 100;
        const tween = { duration: t(0.19), ease: EASE_OUT };
        animate(edgeL, b.l + (Math.min(a.l, b.l) - b.l) * u, tween);
        animate(edgeR, b.r + (Math.max(a.r, b.r) - b.r) * u, tween);
        handoff.current = setTimeout(() => land(to, null, false, true), t(0.15) * 1000);
    };

    const select = i => {
        if (disabled) return;
        const from = committed.current;
        commit(i);
        if (from === i) jumpTo(i);
        else travel(from, i);
    };
    const localX = event => {
        const rect = box.current;
        const scale = rect?.width ? trackRef.current.offsetWidth / rect.width : 1;
        return (event.clientX - (rect ? rect.left : 0)) * scale - inset;
    };

    const handlePointerDown = (event, i) => {
        if (disabled || drag.current || event.button !== 0) return;
        suppressClick.current = false;
        box.current = trackRef.current.getBoundingClientRect();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const x = localX(event);
        const onThumb = draggable && x >= edgeL.get() && x <= edgeR.get();
        drag.current = {
            id: event.pointerId, x0: x, slot: i, onThumb, live: false,
            offset: 0, w: 0, hist: [[event.timeStamp, x]],
        };
        if (onThumb) {
            clearTimeout(handoff.current);
            gen.current += 1;
            edgeL.stop();
            edgeR.stop();
        } else if (!reduce) {
            event.currentTarget.dataset.pressed = "";
        }
    };

    const handlePointerMove = event => {
        const d = drag.current;
        if (!d || event.pointerId !== d.id || !d.onThumb) return;
        const x = localX(event);
        d.hist.push([event.timeStamp, x]);
        if (d.hist.length > 8) d.hist.shift();
        if (!d.live) {
            if (Math.abs(x - d.x0) < 4) return;
            d.live = true;
            d.offset = d.x0 - edgeL.get();
            d.w = edgeR.get() - edgeL.get();
            trackRef.current.dataset.held = "";
        }
        const width = innerW.get();
        const left = x - d.offset;
        const maxLeft = width - d.w;
        if (reduce) {
            const clamped = clamp(left, 0, maxLeft);
            edgeL.set(clamped);
            edgeR.set(clamped + d.w);
        } else if (left < 0) {
            edgeL.set(0);
            edgeR.set(d.w - rubber(-left, d.w));
        } else if (left > maxLeft) {
            edgeR.set(width);
            edgeL.set(maxLeft + rubber(left - maxLeft, d.w));
        } else {
            edgeL.set(left);
            edgeR.set(left + d.w);
        }
    };

    const release = () => {
        const d = drag.current;
        drag.current = null;
        delete trackRef.current.dataset.held;
        const item = itemRefs.current[d.slot];
        if (item) delete item.dataset.pressed;
        return d;
    };

    const handlePointerUp = event => {
        const d = drag.current;
        if (!d || event.pointerId !== d.id) return;
        release();
        suppressClick.current = d.live || Math.abs(localX(event) - d.x0) > 10;
        if (!d.live) return;
        const velocity = velocityOf(d.hist, event.timeStamp);
        const flick = glide > 0 && Math.abs(velocity) > 110;
        let to = nearestSlot(slots.current, (edgeL.get() + edgeR.get()) / 2 + project(velocity, glide));
        if (flick && to === committed.current) to = clamp(to + Math.sign(velocity), 0, list.length - 1);
        commit(to);
        itemRefs.current[to]?.focus();
        if (reduce) jumpTo(to);
        else land(to, velocity, flick, flick);
    };

    const handlePointerCancel = event => {
        const d = drag.current;
        if (!d || event.pointerId !== d.id) return;
        release();
        suppressClick.current = true;
        if (reduce) jumpTo(index);
        else land(index, null, false, false);
    };

    const handleKeyDown = event => {
        if (disabled || !list.length) return;
        const last = list.length - 1;
        let next = null;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") next = Math.min(last, index + 1);
        else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = Math.max(0, index - 1);
        else if (event.key === "Home") next = 0;
        else if (event.key === "End") next = last;
        if (next === null) return;
        event.preventDefault();
        if (next === index) return;
        commit(next);
        jumpTo(next);
        itemRefs.current[next]?.focus();
    };

    const preset = SIZES[size] || SIZES.md;
    return (
        <div
            ref={trackRef}
            role="radiogroup"
            aria-label={ariaLabel}
            aria-disabled={disabled || undefined}
            data-equal={equalSlots ? "" : undefined}
            data-draggable={draggable && !disabled ? "" : undefined}
            className={`rubber-segment${className ? ` ${className}` : ""}`}
            style={{
                "--rs-track": trackColor,
                "--rs-thumb": thumbColor,
                "--rs-ink": textColor,
                "--rs-ink-active": activeTextColor,
                "--rs-radius": `${radius}px`,
                "--rs-inset": `${inset}px`,
                "--rs-thumb-radius": `${thumbRadius}px`,
                "--rs-h": `${preset.height}px`,
                "--rs-font": `${preset.font}px`,
                "--rs-pad": `${preset.pad}px`,
                "--rs-min": `${preset.min}px`,
            }}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onLostPointerCapture={handlePointerCancel}
        >
            {list.map((item, i) => (
                <button
                    key={item.value}
                    ref={element => { itemRefs.current[i] = element; }}
                    type="button"
                    role="radio"
                    aria-checked={i === index}
                    tabIndex={i === index ? 0 : -1}
                    disabled={disabled}
                    className="rubber-segment__item"
                    onPointerDown={event => handlePointerDown(event, i)}
                    onClick={event => {
                        // A drag also emits a click on the captured button.
                        if (!suppressClick.current || event.detail === 0) select(i);
                        suppressClick.current = false;
                    }}
                    onKeyDown={handleKeyDown}
                >
                    {item.icon}
                    {item.label}
                </button>
            ))}
            <motion.div className="rubber-segment__thumb" aria-hidden="true" style={{ clipPath }}>
                {list.map(item => (
                    <span key={item.value} className="rubber-segment__item rubber-segment__copy">
                        {item.icon}
                        {item.label}
                    </span>
                ))}
            </motion.div>
        </div>
    );
}
